import React from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

interface ScrollRevealProps {
  children: React.ReactNode;
  /** Tailwind / utility classes for the wrapper element. */
  className?: string;
  /** Stagger index 1-8. Adds .velaigal-stagger-N for sequential reveals. */
  stagger?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  /** Render as something other than <div>. */
  as?: 'div' | 'section' | 'article' | 'header' | 'footer';
  /** Override IntersectionObserver threshold. Default 0.15 (15% visible). */
  threshold?: number;
  /** Override rootMargin. Default '0px 0px -10% 0px'. */
  rootMargin?: string;
}

/*
 * Editorial scroll reveal wrapper. Apply to any block whose entrance
 * should feel "discovered" rather than "popped in."
 *
 * Usage:
 *   <ScrollReveal as="section" className="py-20">
 *     <h2>Section heading</h2>
 *     <p>Body…</p>
 *   </ScrollReveal>
 *
 * For a grid of cards, wrap each one with `stagger={i + 1}` so they
 * cascade in:
 *   {items.map((it, i) => (
 *     <ScrollReveal key={it.id} stagger={(i + 1) as 1 | 2 | 3}>
 *       <Card />
 *     </ScrollReveal>
 *   ))}
 */
const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  className = '',
  stagger,
  as: Tag = 'div',
  threshold,
  rootMargin,
}) => {
  const ref = useScrollReveal<HTMLDivElement>({ threshold, rootMargin });
  const staggerClass = stagger ? `velaigal-stagger-${stagger}` : '';
  const composed = `velaigal-on-scroll ${staggerClass} ${className}`.trim();

  return (
    <Tag ref={ref as React.Ref<HTMLDivElement>} className={composed}>
      {children}
    </Tag>
  );
};

export default ScrollReveal;
